import { BaseSyntheticEvent, useCallback, useMemo, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import {
  useAddRideMutation,
  useAddRideWithSmsMutation,
  useDeleteRideScheduleMutation,
  useDeleteRideInstanceMutation,
  useDeleteRideMutation,
  useEditRideMutation,
  usePayDateMutation,
  useRidesByDateQuery,
  useCancelRideMutation,
} from "../../queries/rides.query";
import { Ride } from "../../queries/models/ride.model";
import { PlanRideForm } from "./PlanRideForm";
import { RideCreationSmsOptions } from "../../queries/models/ride-creation-sms-options.model";
import { useActiveHorsesAsSelectOptionQuery } from "../../queries/horses.query";
import { useActiveRidersAsSelectOptionQuery } from "../../queries/riders.query";
import { Calendar, CalendarChangeEvent } from "primereact/calendar";
import { PlanRow } from "./PlanRow";
import { useTreatmentsByDateQuery } from "../../queries/treatments.query";
import {
  addDays,
  addHours,
  differenceInHours,
  isSameDay,
  parseISO,
  set,
} from "date-fns";
import {
  useActiveUsersAsSelectOptionQuery,
  useUsersQuery,
} from "../../queries/users.query";
import { Button } from "primereact/button";
import { authService } from "../../services/auth.service";
import { useActiveSkillLevelsAsSelectOptionQuery } from "../../queries/skill-levels.query";
import { useActiveRideTypesAsSelectOptionQuery } from "../../queries/ride-types.query";
import {
  useGetAppSettingsQuery,
  useSetAppSettingsMutation,
} from "../../queries/appsettings.query";
import { UserRole } from "../../queries/models/enums/user-role";
import { UserSettingsForm } from "./UserSettingsForm";
import { useGetUserByIdQuery } from "../../queries/models/_shared/auth.query";
import { ChangeInstructorsForm } from "./ChangeInstructorsForm";
import { User } from "../../queries/models/user.model";
import { PlanLegend } from "./PlanLegend";
import { DeleteRidesForm } from "./DeleteRidesForm";
import { useHorsesQuery } from "../../queries/horses.query";
import { useHorseGroupsQuery } from "../../queries/horse-groups.query";

export function Plan() {
  const queryClient = useQueryClient();
  const horsesOptionsQuery = useActiveHorsesAsSelectOptionQuery();
  const ridersOptionsQuery = useActiveRidersAsSelectOptionQuery();
  const usersOptionsQuery = useActiveUsersAsSelectOptionQuery();
  const skillLevelOptionsQuery = useActiveSkillLevelsAsSelectOptionQuery();
  const usersQuery = useUsersQuery();
  const rideTypeOptionsQuery = useActiveRideTypesAsSelectOptionQuery();
  const userQuery = useGetUserByIdQuery(authService.getLoggedUser()?.id);
  const appSettingsQuery = useGetAppSettingsQuery();
  const addRideMutation = useAddRideMutation();
  const addRideWithSmsMutation = useAddRideWithSmsMutation();
  const editRideMutation = useEditRideMutation();
  const payAllDayMutation = usePayDateMutation();
  const deleteRideMutation = useDeleteRideMutation();
  const cancelRideMutation = useCancelRideMutation();
  const deleteRideScheduleMutation = useDeleteRideScheduleMutation();
  const deleteRideInstanceMutation = useDeleteRideInstanceMutation();
  const toast = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>(
    new Date(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
      12,
      0,
      0,
      0
    )
  );
  const [selectedRide, setSelectedRide] = useState<Ride>();
  const [selectedInstructor, setSelectedInstructor] = useState<
    User | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [
    isDeleteWithScheduleConfirmationModalOpen,
    setIsDeleteWithScheduleConfirmationModalOpen,
  ] = useState<boolean>(false);
  const [payAllConfirmationModalOpen, setPayAllConfirmationModalOpen] =
    useState<boolean>(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] =
    useState<boolean>(false);
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false);
  const [isNoRidesModalOpen, setIsNoRidesModalOpen] = useState<boolean>(false);
  const [isChangeInstructorsModalOpen, setIsChangeInstructorsModalOpen] =
    useState<boolean>(false);
  const [isDeleteRidesModalOpen, setIsDeleteRidesModalOpen] =
    useState<boolean>(false);

  const hours = useMemo(() => {
    let result = [];
    for (let i = 6; i <= 22; i++) {
      result.push(i);
    }

    return result;
  }, []);

  const isAdmin = useMemo(() => {
    return authService.isAdmin();
  }, []);

  const canBeCancelled = useMemo(() => {
    return selectedRide ? !selectedRide.isScheduled : false;
  }, [selectedRide]);

  const isAdminOrInstructor = useMemo(() => {
    return authService.isAdmin() || authService.isInstructor();
  }, []);

  const isLessThan24h = useMemo(() => {
    if (!selectedRide?.date) return false;

    const rideDate =
      typeof selectedRide.date === "string"
        ? parseISO(selectedRide.date)
        : new Date(selectedRide.date);

    return differenceInHours(rideDate, new Date()) < 24;
  }, [selectedRide]);

  const ridesQuery = useRidesByDateQuery(selectedDate);
  const treatmentsQuery = useTreatmentsByDateQuery(selectedDate);
  const horsesQuery = useHorsesQuery();
  const horseGroupsQuery = useHorseGroupsQuery();

  const visibleRides = useMemo(() => {
    const today = new Date();
    const user = userQuery.data;
    const onlyLoggedUserRides = user?.usePlanOnlyMyRides ?? false;

    var result =
      ridesQuery.data?.filter(
        (ride) =>
          (isAdminOrInstructor || isSameDay(new Date(ride.date), today)) &&
          (!onlyLoggedUserRides || ride.userId === user?.id)
      ) ?? [];

    return result;
  }, [isAdminOrInstructor, ridesQuery.data, userQuery.data]);

  const instructors = useMemo(() => {
    if (!usersQuery.data) return [];
    return usersQuery.data?.filter(
      (user) =>
        user.roles === UserRole.Instructor || user.roles === UserRole.Admin
    );
  }, [usersQuery.data]);

  const handleAddNewRide = (date: Date) => {
    setSelectedRide({
      id: 0,
      name: "",
      comment: "",
      date: date,
      isPaid: false,
      horseId: undefined,
      riderId: undefined,
      horse: undefined,
      rider: undefined,
      userId: undefined,
      user: undefined,
      hasIssue: false,
      skillLevelId: undefined,
      hasFall: false,
      isCancelled: false,
      duration: 1,
    } as Ride);
    setIsModalOpen(true);
  };

  const handleEditRide = (ride: Ride) => {
    setSelectedRide(ride);
    setIsModalOpen(true);
  };

  const handlePayAllDay = useCallback(() => {
    setPayAllConfirmationModalOpen(true);
  }, []);

  const handlePayAllDayConfirmed = useCallback(() => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries("rides");
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setPayAllConfirmationModalOpen(false);
      },
    };

    return payAllDayMutation.mutateAsync(selectedDate, mutateOptions);
  }, [payAllDayMutation, queryClient, selectedDate, toast]);

  const handleSaveRide = (data: Ride, smsOptions?: RideCreationSmsOptions) => {
    return data?.id || data?.scheduledRideId
      ? editExistingRide(data)
      : saveNewRide(data, smsOptions);
  };

  const confirmPayAllDayDialogProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Upewnij się że wszystkie dziejsze jazdy się odbyły. Jeśli tak potwiedz, zostaną opłacone a kredyty odjęte.",
    height: "200px",
    width: "80%",
    isOpen: payAllConfirmationModalOpen,
    confirmation: true,
    onClose: () => setPayAllConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handlePayAllDayConfirmed,
  };

  const saveNewRide = (data: Ride, smsOptions?: RideCreationSmsOptions) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedRide(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries("rides");
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: Ride = {
      id: data.id,
      comment: data.comment,
      date: data.date,
      isPaid: data.isPaid,
      horseId: data.horseId === 0 ? undefined : data.horseId,
      riderId: data.riderId,
      userId: data.userId === 0 || !data.userId ? undefined : data.userId,
      isScheduled: data.isScheduled,
      weeksRepetition: data.isScheduled ? data.weeksRepetition ?? 1 : undefined,
      rideTypeId: data.rideTypeId,
      hasIssue: data.hasIssue,
      hasFall: data.hasFall,
      skillLevelId: data.skillLevelId,
      isCancelled: data.isCancelled,
      duration: data.duration,
    };

    if (smsOptions) {
      return addRideWithSmsMutation.mutateAsync(
        { request, options: smsOptions },
        mutateOptions
      );
    }

    return addRideMutation.mutateAsync(request, mutateOptions);
  };

  const editExistingRide = (data: Ride) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedRide(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries("rides");
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: Ride = {
      id: data.id,
      comment: data.comment,
      date: data.date,
      isPaid: data.isPaid,
      horseId: data.horseId === 0 ? undefined : data.horseId,
      riderId: data.riderId,
      userId: data.userId === 0 || !data.userId ? undefined : data.userId,
      rideTypeId: data.rideTypeId,
      isScheduled: data.isScheduled,
      scheduledRideId: data.scheduledRideId,
      hasIssue: data.hasIssue,
      hasFall: data.hasFall,
      skillLevelId: data.skillLevelId,
      isCancelled: data.isCancelled,
      duration: data.duration,
    };

    return editRideMutation.mutateAsync(request, mutateOptions);
  };

  const mobileHorseFormModalProps: CustomModalProps = {
    header:
      selectedRide?.id || selectedRide?.scheduledRideId
        ? "Jazda szczegóły"
        : "Dodaj nowa jazde",
    onClose: () => {
      setSelectedRide(undefined);
      setIsModalOpen(false);
    },
    isOpen: isModalOpen,
    body: (
      <PlanRideForm
        ride={selectedRide ?? ({} as Ride)}
        onSave={handleSaveRide}
        horseOptions={horsesOptionsQuery.data ?? []}
        riderOptions={ridersOptionsQuery.data ?? []}
        userOptions={usersOptionsQuery.data ?? []}
        skillLevelOptions={skillLevelOptionsQuery.data ?? []}
        rideTypeOptions={rideTypeOptionsQuery.data ?? []}
        onCancel={() => {
          setSelectedRide(undefined);
          setIsModalOpen(false);
        }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        onDeleteWithSchedule={() =>
          setIsDeleteWithScheduleConfirmationModalOpen(true)
        }
        isSubmitting={isSubmitting}
      />
    ),
  };

  const mobileChangeInstructorsFormModalProps: CustomModalProps = {
    header: "Zmień instruktora",
    onClose: () => {
      setIsChangeInstructorsModalOpen(false);
    },
    isOpen: isChangeInstructorsModalOpen,
    body: (
      <ChangeInstructorsForm
        date={selectedDate}
        onSave={async (instructor, selectedRides) => {
          setIsChangeInstructorsModalOpen(false);
          if (selectedRides.length > 0) {
            const ridesToUpdate = selectedRides.map((ride) => ({
              ...ride,
              userId: instructor.id,
            }));
            await Promise.all(
              ridesToUpdate.map((ride) => {
                ride?.id || ride?.scheduledRideId
                  ? editExistingRide(ride)
                  : saveNewRide(ride);
              })
            );
          }
        }}
        onCancel={() => setIsChangeInstructorsModalOpen(false)}
        instructor={selectedInstructor!}
        rides={visibleRides}
      />
    ),
  };

  const mobileDeleteRidesFormModalProps: CustomModalProps = {
    header: "Usuń jazdy",
    onClose: () => {
      setIsDeleteRidesModalOpen(false);
    },
    isOpen: isDeleteRidesModalOpen,
    body: (
      <DeleteRidesForm
        rides={visibleRides}
        date={selectedDate}
        onCancel={() => {
          setIsDeleteRidesModalOpen(false);
        }}
        onSave={async (selectedRides) => {
          setIsDeleteRidesModalOpen(false);
          if (selectedRides.length > 0) {
            await Promise.all(
              selectedRides.map((ride) => {
                return ride?.id
                  ? deleteRideMutation.mutateAsync(ride.id)
                  : deleteRideInstanceMutation.mutateAsync(ride);
              })
            );
            await queryClient.invalidateQueries("rides");
          }
        }}
      />
    ),
  };

  const mobileSettingsFormModalProps: CustomModalProps = {
    header: "Ustawienia",
    onClose: () => {
      setIsSettingsModalOpen(false);
    },
    isOpen: isSettingsModalOpen,
    body: (
      <UserSettingsForm
        onSave={async () => {
          setIsSettingsModalOpen(false);
        }}
      />
    ),
  };

  const handleDeleteRide = useCallback(() => {
    if (selectedRide) {
      if (selectedRide.id) {
        deleteRideMutation.mutateAsync(selectedRide.id, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("rides");
            toast.current?.show({
              severity: "success",
              detail: "Zapisano!",
            });
            setSelectedRide(undefined);
            setIsDeleteConfirmationModalOpen(false);
            setIsModalOpen(false);
          },
          onError: async (error: any) => {
            toast.current?.show({
              severity: "error",
              detail: error.message,
            });
          },
        });
      } else {
        deleteRideInstanceMutation.mutateAsync(selectedRide, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("rides");
            toast.current?.show({
              severity: "success",
              detail: "Zapisano!",
            });
            setSelectedRide(undefined);
            setIsDeleteConfirmationModalOpen(false);
            setIsModalOpen(false);
          },
          onError: async (error: any) => {
            toast.current?.show({
              severity: "error",
              detail: error.message,
            });
          },
        });
      }
    }
  }, [
    deleteRideMutation,
    deleteRideInstanceMutation,
    queryClient,
    selectedRide,
    toast,
  ]);

  const mobilePlanLegendProps: CustomModalProps = {
    header: "Legenda planu",
    onClose: () => {
      setIsLegendOpen(false);
    },
    isOpen: isLegendOpen,
    body: <PlanLegend />,
    width: "80%",
    confirmation: false,
  };

  const notRiddenHorses = useMemo(() => {
    const groupId = userQuery.data?.showNotRiddenFromGroupId ?? undefined;
    if (!groupId) return [] as typeof horsesQuery.data;
    const horsesInGroup = (horsesQuery.data ?? []).filter((h) =>
      (h.horseGroups ?? []).some((g) => g.id === groupId)
    );
    const riddenIds = new Set(
      (ridesQuery.data ?? [])
        .filter((r) => !r.isCancelled && !!r.horseId)
        .map((r) => r.horseId as number)
    );
    return horsesInGroup.filter((h) => !riddenIds.has(h.id));
  }, [horsesQuery.data, ridesQuery.data, userQuery.data]);

  const mobileNoRidesModalProps: CustomModalProps = {
    header: "Konie na dziś bez jazdy",
    onClose: () => setIsNoRidesModalOpen(false),
    isOpen: isNoRidesModalOpen,
    width: "90%",
    body: (
      <div className="p-2">
        {!userQuery.data?.showNotRiddenFromGroupId && (
          <div className="text-center">
            Wybierz najpierw grupę w Ustawieniach użytkownika.
          </div>
        )}
        {userQuery.data?.showNotRiddenFromGroupId && (
          <div className="text-center mb-2">
            <span className="px-2 py-1 border-round surface-200 text-800 text-sm">
              Grupa:{" "}
              {horseGroupsQuery.data?.find(
                (g) => g.id === userQuery.data?.showNotRiddenFromGroupId
              )?.name ?? `#${userQuery.data?.showNotRiddenFromGroupId}`}
            </span>
          </div>
        )}
        {userQuery.data?.showNotRiddenFromGroupId &&
          (horsesQuery.isLoading || ridesQuery.isLoading) && (
            <div className="flex justify-content-center">
              <Loader type="spinner" />
            </div>
          )}
        {userQuery.data?.showNotRiddenFromGroupId &&
          !horsesQuery.isLoading &&
          !ridesQuery.isLoading &&
          (notRiddenHorses?.length ? (
            <ul className="list-none p-0 m-0">
              {notRiddenHorses.map((h) => (
                <li
                  key={h.id}
                  className="p-2 border-bottom-1 surface-border flex justify-content-between"
                >
                  <span className="font-semibold">{h.name}</span>
                  <span className="text-500">{h.shortName}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center">Wszystkie konie dziś jeżdżą.</div>
          ))}
      </div>
    ),
  };

  const handleCancelRide = useCallback(() => {
    if (!selectedRide || !canBeCancelled) return;
    cancelRideMutation.mutateAsync(selectedRide.id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries("rides");
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        setSelectedRide(undefined);
        setIsDeleteConfirmationModalOpen(false);
        setIsModalOpen(false);
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
    });
  }, [canBeCancelled, cancelRideMutation, queryClient, selectedRide, toast]);

  const handleDeleteWithSchedule = useCallback(() => {
    if (selectedRide) {
      if (selectedRide.id) {
        deleteRideMutation.mutateAsync(selectedRide.id, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("rides");
            toast.current?.show({
              severity: "success",
              detail: "Zapisano!",
            });
            setSelectedRide(undefined);
            setIsDeleteWithScheduleConfirmationModalOpen(false);
          },
          onError: async (error: any) => {
            toast.current?.show({
              severity: "error",
              detail: error.message,
            });
          },
        });
      }

      if (selectedRide.scheduledRideId) {
        deleteRideScheduleMutation.mutateAsync(selectedRide.scheduledRideId!, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("rides");
            setIsDeleteWithScheduleConfirmationModalOpen(false);
            setIsModalOpen(false);
          },
          onError: async (error: any) => {
            toast.current?.show({
              severity: "error",
              detail: error.message,
            });
          },
        });
      }
    }
  }, [
    deleteRideMutation,
    deleteRideScheduleMutation,
    queryClient,
    selectedRide,
    toast,
  ]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: (
      <div>
        {isLessThan24h && (
          <div>
            <span className="text-red-600">
              Do zaplanowanej jazdy pozostało mniej niż 24 godziny. Jeśli ją
              odwołasz, zostanie usunięta z planu jazd, ale pozostanie w
              historii. Spowoduje to odjęcie jednej jazdy z karnetu (jeśli opcja
              ta jest zaznaczona w ustawieniach). Odwołana jazda nie będzie
              uwzględniana w statystykach.{" "}
            </span>
          </div>
        )}
        <div className="mt-4">Czy chcesz usunąć jazde?</div>
      </div>
    ),
    height: canBeCancelled ? "320px" : "160px",
    width: "80%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteRide,
    confirmationButtonText: "Usuń",
    secondaryButtonText: "Odwołaj",
    onSecondary: canBeCancelled ? handleCancelRide : undefined,
  };

  const mobileModalConfirmationAllFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć jazde i wszystkie kolejne?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteWithScheduleConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteWithScheduleConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteWithSchedule,
  };

  const handleSelectedDateChange = useCallback((e: CalendarChangeEvent) => {
    setSelectedDate(addHours(new Date(e.value as string), 12));
  }, []);

  return (
    <>
      {ridesQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full flex flex-column">
          {!!mobileHorseFormModalProps && (
            <CustomModal {...mobileHorseFormModalProps} />
          )}
          {!!mobileModalConfirmationFormProps && (
            <CustomModal {...mobileModalConfirmationFormProps} />
          )}
          {!!mobileModalConfirmationAllFormProps && (
            <CustomModal {...mobileModalConfirmationAllFormProps} />
          )}
          {!!confirmPayAllDayDialogProps && (
            <CustomModal {...confirmPayAllDayDialogProps} />
          )}
          {!!mobileSettingsFormModalProps && (
            <CustomModal {...mobileSettingsFormModalProps} />
          )}
          {!!mobileChangeInstructorsFormModalProps && (
            <CustomModal {...mobileChangeInstructorsFormModalProps} />
          )}
          {!!mobilePlanLegendProps && (
            <CustomModal {...mobilePlanLegendProps} />
          )}
          {!!mobileNoRidesModalProps && (
            <CustomModal {...mobileNoRidesModalProps} />
          )}
          {!!mobileDeleteRidesFormModalProps && (
            <CustomModal {...mobileDeleteRidesFormModalProps} />
          )}

          <div className="p-2 flex gap-2">
            <div className="w-full">
              <Calendar
                className="w-full"
                locale="pl"
                dateFormat="DD dd MM yy"
                onChange={handleSelectedDateChange}
                value={selectedDate}
              />
            </div>
            <div className="">
              <Button
                className="w-full px-3"
                type="button"
                outlined
                icon="pi pi-info-circle"
                onClick={() => {
                  setIsLegendOpen(true);
                }}
              />
            </div>
            <div className="">
              <Button
                className="w-full px-3"
                type="button"
                outlined
                icon="pi pi-list"
                onClick={() => setIsNoRidesModalOpen(true)}
              />
            </div>
            {isAdminOrInstructor && (
              <div className="">
                <Button
                  className="w-full px-3"
                  type="button"
                  outlined
                  icon="pi pi-wrench"
                  onClick={() => {
                    setIsSettingsModalOpen(true);
                  }}
                />
              </div>
            )}
          </div>
          <div className="p-2 flex">
            <div className="w-6 px-2">
              <Button
                className="w-full"
                type="button"
                icon="pi pi-arrow-left"
                label="Poprzedni"
                onClick={() => {
                  setSelectedDate(addDays(selectedDate, -1));
                }}
              />
            </div>
            <div className="w-6 px-2">
              <Button
                className="w-full"
                type="button"
                icon="pi pi-arrow-right"
                iconPos="right"
                label="Nastepny"
                onClick={() => {
                  setSelectedDate(addDays(selectedDate, 1));
                }}
              />
            </div>
          </div>
          <div className="p-2 flex justify-content-between items-start">
            <div className="flex flex-wrap gap-2 text-normal-color align-items-center">
              {instructors?.map((instructor) => (
                <div
                  key={instructor.id}
                  className="p-2 rounded-lg text-center text-sm font-semibold"
                  style={{
                    width: "fit-content",
                    borderRadius: "8px",
                    backgroundColor: instructor?.color ?? "#ffffff",
                  }}
                  onClick={() => {
                    if (!isAdminOrInstructor) return;
                    setSelectedInstructor(instructor);
                    setIsChangeInstructorsModalOpen(true);
                  }}
                >
                  {instructor.name}
                </div>
              ))}
            </div>
            <div className="ml-2">
              {isAdmin && (
                <Button
                  className="w-full"
                  type="button"
                  icon="pi pi-trash"
                  label="Usuń"
                  severity="danger"
                  outlined
                  onClick={() => setIsDeleteRidesModalOpen(true)}
                />
              )}
            </div>
          </div>

          <div
            className="overflow-auto h-full flex-1"
            style={{ height: "calc(100% - 100px)" }}
          >
            {hours.map((hour) => (
              <PlanRow
                key={hour}
                onAddNewRide={handleAddNewRide}
                onEditRide={handleEditRide}
                date={new Date(new Date(selectedDate).setHours(hour))}
                rides={visibleRides}
                treatments={
                  userQuery.data?.showTreatmentsInPlan ?? false
                    ? treatmentsQuery.data ?? []
                    : []
                }
                shortNames={userQuery.data?.usePlanShortcuts ?? false}
                showInstructor={appSettingsQuery.data?.showPlanUser ?? false}
              />
            ))}

            {isAdmin && (
              <div className="w-full p-2">
                <Button
                  className="settings-button w-full"
                  type="button"
                  icon="pi pi-save"
                  label="Opłać cały dzień"
                  onClick={handlePayAllDay}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
